/**
 * Trading-domain time formatting contract (2026-07-04).
 *
 * Transport is canonical epoch **milliseconds**; trading-domain display
 * is always America/New_York and must never follow the observer's
 * browser timezone — a trading UI whose timestamps drift with the
 * viewer mixes three clocks on one screen (browser-local renders,
 * host-localtime backend strings, ET-labeled copy) and reads as
 * "time is never consistent".
 *
 * Components MUST NOT call `new Date(x).toLocaleTimeString()` directly;
 * they go through these helpers:
 *   - normalizeEpochMs(x): epoch s/ms/numeric-string → ms | null
 *   - fmtET(ms, locale, nowMs?): "15:31:46 ET", auto "07-02 15:31:46 ET"
 *     when the timestamp is not the same ET calendar day as nowMs
 *   - fmtETDateTime(ms, locale): "2026-07-02 15:31:46 ET"
 *   - fmtAge(ms, pageNowMs, locale): "21 秒前" / "3 分钟前" / "2 小时前";
 *     older than 24h falls back to fmtETDateTime
 */

export const ET_TZ = 'America/New_York';

type Loc = 'zh' | 'en';

/** >1e12 → already ms; 1e9..1e12 → seconds, scale ×1000; ISO strings parse. */
export function normalizeEpochMs(x: unknown): number | null {
  if (typeof x === 'string') {
    const trimmed = x.trim();
    if (!trimmed) return null;
    const numeric = Number(trimmed);
    if (Number.isFinite(numeric) && numeric > 0) {
      if (numeric > 1e12) return Math.round(numeric);
      if (numeric >= 1e9) return Math.round(numeric * 1000);
    }
    const parsed = Date.parse(trimmed);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  const n = typeof x === 'number' ? x : NaN;
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n > 1e12) return Math.round(n);
  if (n >= 1e9) return Math.round(n * 1000);
  return null;
}

function etParts(ms: number): { ymd: string; md: string; hms: string } {
  const d = new Date(ms);
  // en-CA yields ISO-like YYYY-MM-DD; force stable numeric fields.
  const ymd = d.toLocaleDateString('en-CA', {
    timeZone: ET_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  });
  const hms = d.toLocaleTimeString('en-GB', {
    timeZone: ET_TZ, hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  return { ymd, md: ymd.slice(5), hms };
}

/** Bare "HH:MM:SS" pinned to ET — for dense consoles that append their
 * own sub-second suffix; the surrounding column/header must carry the
 * ET label since this string deliberately omits it. */
export function etHms(msLike: unknown): string {
  const ms = normalizeEpochMs(msLike);
  if (ms === null) return '—';
  return etParts(ms).hms;
}

/** "15:31:46 ET" same ET day as nowMs, else "07-02 15:31:46 ET". */
export function fmtET(msLike: unknown, _locale: Loc = 'zh', nowMs?: number): string {
  const ms = normalizeEpochMs(msLike);
  if (ms === null) return '—';
  const p = etParts(ms);
  const now = etParts(nowMs ?? Date.now());
  return p.ymd === now.ymd ? `${p.hms} ET` : `${p.md} ${p.hms} ET`;
}

/** "2026-07-02 15:31:46 ET" — full absolute form. */
export function fmtETDateTime(msLike: unknown, _locale: Loc = 'zh'): string {
  const ms = normalizeEpochMs(msLike);
  if (ms === null) return '—';
  const p = etParts(ms);
  return `${p.ymd} ${p.hms} ET`;
}

/** Freshness relative to the single page clock (lastFetch). */
export function fmtAge(msLike: unknown, pageNowMs: number, locale: Loc = 'zh'): string {
  const ms = normalizeEpochMs(msLike);
  if (ms === null) return '—';
  const diff = Math.max(0, pageNowMs - ms);
  if (diff >= 24 * 3600_000) return fmtETDateTime(ms, locale);
  const zh = locale === 'zh';
  if (diff < 60_000) {
    const s = Math.round(diff / 1000);
    return zh ? `${s} 秒前` : `${s}s ago`;
  }
  if (diff < 3600_000) {
    const m = Math.round(diff / 60_000);
    return zh ? `${m} 分钟前` : `${m}m ago`;
  }
  const h = Math.round(diff / 3600_000);
  return zh ? `${h} 小时前` : `${h}h ago`;
}
